import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, MessageCircle, CheckCircle2, FileDown, Mail, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const waLink = (phone, title) => {
  const digits = (phone || '').replace(/\D/g, '');
  const num = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${num}?text=${encodeURIComponent(`Hi, this is Bharat Estate regarding your interest in ${title || 'our listing'}.`)}`;
};

const statusStyle = { new: 'bg-[#D4AF37] text-[#0F172A]', contacted: 'bg-emerald-100 text-emerald-800', closed: 'bg-slate-200 text-slate-700' };

export const LeadsInbox = ({ onCount }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/leads?limit=200`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setLeads(d.leads || []);
      onCount?.(d.new_count || 0);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [onCount]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (lead, status) => {
    const res = await fetch(`${API}/admin/leads/${lead.lead_id}/status`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) { toast.error('Could not update lead'); return; }
    setLeads((ls) => ls.map((l) => (l.lead_id === lead.lead_id ? { ...l, status } : l)));
    onCount?.(leads.filter((l) => l.lead_id !== lead.lead_id && l.status === 'new').length + (status === 'new' ? 1 : 0));
    toast.success(status === 'contacted' ? 'Marked as contacted' : 'Lead updated');
  };

  const shown = leads.filter((l) => view === 'all' || (view === 'new' ? l.status === 'new' : l.kind === view));

  return (
    <div data-testid="leads-inbox">
      <div className="flex flex-wrap gap-2 mb-4">
        {[['all', 'All'], ['new', 'New'], ['brochure', 'Brochure'], ['enquiry', 'Enquiries']].map(([v, label]) => (
          <Button key={v} size="sm" variant={view === v ? 'default' : 'outline'} onClick={() => setView(v)} data-testid={`leads-filter-${v}`}>{label}</Button>
        ))}
        <Button size="sm" variant="ghost" onClick={load} className="ml-auto gap-1" data-testid="leads-refresh"><RotateCcw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading leads…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center" data-testid="leads-empty">No leads yet.</p>
      ) : (
        <div className="space-y-3">
          {shown.map((l) => (
            <div key={l.lead_id} className="bg-white border border-border/60 rounded-sm p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`lead-row-${l.lead_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#0F172A]" data-testid={`lead-name-${l.lead_id}`}>{l.name}</span>
                  <Badge variant="outline" className="gap-1 text-[10px] uppercase tracking-wider">
                    {l.kind === 'brochure' ? <FileDown className="h-3 w-3" /> : <Mail className="h-3 w-3" />} {l.kind}
                  </Badge>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${statusStyle[l.status] || statusStyle.new}`} data-testid={`lead-status-${l.lead_id}`}>{l.status}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1 truncate">{l.property_title || l.property_id || 'General enquiry'}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{l.phone}{l.email ? ` · ${l.email}` : ''} · {new Date(l.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                {l.message && <p className="text-sm mt-2 text-[#0F172A]/80 line-clamp-2">“{l.message}”</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                {l.phone && (
                  <>
                    <Button asChild size="sm" variant="outline" data-testid={`lead-call-${l.lead_id}`}><a href={`tel:+${l.phone.replace(/\D/g, '').replace(/^(\d{10})$/, '91$1')}`}><Phone className="h-4 w-4 mr-1" /> Call</a></Button>
                    <Button asChild size="sm" className="bg-[#25D366] hover:bg-[#1ebe5b] text-white" data-testid={`lead-whatsapp-${l.lead_id}`}><a href={waLink(l.phone, l.property_title)} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</a></Button>
                  </>
                )}
                {l.status === 'new' ? (
                  <Button size="sm" onClick={() => setStatus(l, 'contacted')} className="btn-gold" data-testid={`lead-contacted-${l.lead_id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> Contacted</Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setStatus(l, 'new')} data-testid={`lead-reopen-${l.lead_id}`}>Reopen</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
